import { Link } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import type { Notice } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";

function formatDate(d?: string): string {
  if (!d) return "";
  return d.slice(0, 10).replace(/-/g, ".");
}

export default function NoticeSection() {
  const { data, loading, error } = useApi<Notice[]>("notices.php?size=3");
  const notices = (data ?? []).slice(0, 3);

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              최신 공지
            </h2>
            <p className="mt-2 text-foreground-600">클럽의 새로운 소식을 전해드립니다.</p>
          </div>
          <Link
            to="/notices"
            className="whitespace-nowrap inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-semibold text-primary-700 hover:bg-primary-100 transition-colors"
          >
            전체보기
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState icon="ri-error-warning-line" title="공지를 불러오지 못했습니다" description={error} />
        ) : notices.length === 0 ? (
          <EmptyState icon="ri-file-list-3-line" title="등록된 공지가 없습니다" />
        ) : (
          <div className="divide-y divide-background-200 rounded-xl border border-background-200 bg-background-50">
            {notices.map((n) => (
              <Link
                key={n.no}
                to={`/notices/${n.no}`}
                className="flex items-center gap-4 p-4 md:p-5 hover:bg-background-100 transition-colors"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-megaphone-line text-lg" aria-hidden="true"></i>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {(n.fixed === 1 || n.is_fixed === 1 || n.top === 1) && (
                      <span className="whitespace-nowrap rounded-full bg-secondary-500 px-2 py-0.5 text-[11px] font-semibold text-primary-950">
                        고정
                      </span>
                    )}
                    <span className="truncate font-medium text-foreground-900">{n.title}</span>
                  </div>
                  <div className="mt-1 text-xs text-foreground-500">
                    {formatDate(n.reg_date || n.date)}
                  </div>
                </div>
                <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}